import type { FieldPath, FieldValues } from 'react-hook-form';
import type { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import type { PatternFormField, PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { getStringValue, isNotNull } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { MetaDataValueSet } from '~/common/model/api/batch-type';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type { ImportConditionRule, ImportSettings, ImportSettingsOutlook } from '~/common/model/api/batch-type/process-flow';
import type { DataTypeGeneral, DataTypeList, DataValue, DataValueNull } from '~/common/model/api/common';
import type { TransformImportSettings } from '~/services/server/api/batch-type/mapper';
import type { SettingsBatchTypeLibraryFormSubmitData } from '~/components/feature/settings/batch-type-library/form';

import { DATA_TYPE_GENERAL_NAMES, METADATA_CREATE_WITH_DATA_VALUE } from '~/common/constants/feature/settings/batch-type-library';
import commonLogic from '~/common/logic/common';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

import type { TaskImportValidationRules } from './_import.type';

import { isIntegrationDrive, isIntegrationFolder } from './_common';

const logger = createLogger('logic/feature/settings/batch-type-library/processFlow/step/task/import');

// * Transformation to text is not necessary
const IS_TRANSFORMATION_TEXT = false;

/**
 * Create Pattern from Import FormFieldItem.
 *
 * @param {PatternFormField} formField Import FormFieldItem.
 * @param {SettingsBatchTypeLibraryFormSubmitData} batchTypeData SettingsBatchTypeLibraryFormSubmitData.
 *
 * @returns {Pattern[]} Import Pattern.
 */
export function createImportPattern(formField: PatternFormField, batchTypeData?: SettingsBatchTypeLibraryFormSubmitData): Pattern[] {
  const scope: PatternAccessScope = 'document';

  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern =
            item.value === 'batchType'
              ? patternLogic.createPattern.fromGlobalBatchType({ ...item, value: batchTypeData?.name ?? 'batchType' }, IS_TRANSFORMATION_TEXT)
              : patternLogic.createPattern.fromSystemValue(item, 'batch', IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create Import FormFieldItem from Pattern.
 *
 * @param {Pattern[]} importPattern Pattern.
 *
 * @returns {PatternFormField} Import FormFieldItem.
 */
export function createImportFormFieldItem(importPattern: Pattern[]): PatternFormField {
  return importPattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        const batchType = patternLogic.global.patternValue.globalBatchType.fromPath(pattern.path);

        item = batchType
          ? patternLogic.createPatternFormFieldItem.fromPatternGlobalBatchType(pattern)
          : patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Check is Import Settings.
 *
 * @template TSettings TSettings.
 * @param {ImportSettings} settings Settings.
 * @param {TSettings['destination']['integration']['$is']} type Type.
 *
 * @returns {boolean} Returns `true` if the `settings` object matches the specified type, otherwise `false`.
 */
export function isImportSettings<TSettings extends ImportSettings>(
  settings: ImportSettings | undefined,
  type: TSettings['destination']['integration']['$is'],
): settings is TSettings {
  if (!settings) {
    return false;
  }

  return settings.destination.integration.$is === type;
}

/**
 * Check transform is Import Settings.
 *
 * @template TSettings TSettings.
 * @param {TransformImportSettings} settings Settings.
 * @param {TSettings['destination']['integration']['provider']} type Type.
 *
 * @returns {boolean} Returns `true` if the `settings` object matches the specified type, otherwise `false`.
 */
export function isTransformImportSettings<TSettings extends TransformImportSettings>(
  settings: TransformImportSettings,
  type: TSettings['destination']['integration']['provider'],
): settings is TSettings {
  return settings.destination.integration.provider === type;
}

/**
 * Create Condition from Condition FormField.
 *
 * @param {CompositeFilterDescriptor} conditionFormField Condition FormField.
 * @param {MetaDataValueSet} metaDatas MetaDataValueSet.
 * @param {SettingsBatchTypeLibraryFormSubmitData} batchTypeData SettingsBatchTypeLibraryFormSubmitData.
 *
 * @returns {ImportSettingsOutlook['condition']} Condition.
 */
export function createCondition(
  conditionFormField: CompositeFilterDescriptor,
  metaDatas: MetaDataValueSet[],
  batchTypeData?: SettingsBatchTypeLibraryFormSubmitData,
): ImportSettingsOutlook['condition'] {
  return (
    (function build(descriptor: FilterDescriptor | CompositeFilterDescriptor): Nullable<ImportConditionRule> {
      if (isCompositeFilterDescriptor(descriptor)) {
        return {
          group: descriptor.logic,
          rules: descriptor.filters.map((filter) => build(filter)).filter((filter): filter is ImportConditionRule => !!filter),
        };
      }

      const metaData = metaDatas.find(({ name }) => name === descriptor.field);
      if (!metaData) {
        logger.writeWithData('warn', 'Not found metaData', descriptor);
        return undefined;
      }

      const metaDataType: DataTypeGeneral | DataTypeList = {
        name: metaData.type,
      };

      return {
        field: getStringValue(descriptor.field) ?? '',
        operator: getStringValue(descriptor.operator),
        value: METADATA_CREATE_WITH_DATA_VALUE.includes(getStringValue(descriptor.field))
          ? fieldLogic.createDataValue(metaDataType, descriptor.value)
          : createImportPattern(descriptor.value, batchTypeData),
      };
    })(conditionFormField) ?? {
      group: 'and',
      rules: [],
    }
  );
}

/**
 * Create value from field value.
 *
 * @param {DataValue | DataValueNull | undefined} fieldValue Field value.
 *
 * @returns {DataValue['value']} Value.
 */
function createValue(fieldValue: DataValue | DataValueNull | undefined): DataValue['value'] {
  if (!fieldValue?.type) {
    return null;
  }

  const { type, value } = fieldValue;

  if (DATA_TYPE_GENERAL_NAMES.includes(type)) {
    return value;
  }

  const listFieldType = commonLogic.type.getListDataType(type);
  if (listFieldType && Array.isArray(value)) {
    return value;
  }

  return null;
}

/**
 * Create Condition FormField.
 *
 * @param {ImportConditionRule} condition Condition.
 *
 * @returns {CompositeFilterDescriptor} Condition FormField.
 */
export function createConditionFormField(condition: ImportConditionRule): CompositeFilterDescriptor {
  const filter = (function build(source: ImportConditionRule): FilterDescriptor | CompositeFilterDescriptor {
    if ('group' in source) {
      return {
        logic: source.group,
        filters: source.rules?.map(build) ?? [],
      };
    }

    const { field, operator, value } = source;

    return {
      field,
      operator,
      value: METADATA_CREATE_WITH_DATA_VALUE.includes(field) ? createValue(value as DataValue | DataValueNull) : createImportFormFieldItem(value as Pattern[]),
    };
  })(condition);

  if (isCompositeFilterDescriptor(filter)) {
    return filter;
  }

  return {
    logic: 'and',
    filters: [filter],
  };
}

/**
 * Use Task Import ValidationRules.
 *
 * @template TFieldValues FieldValues object.
 * @template TFieldName Field name.
 * @returns {TaskImportValidationRules<TFieldValues, TFieldName>} TaskImportValidationRules.
 */
export function useTaskImportValidationRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(): TaskImportValidationRules<TFieldValues, TFieldName> {
  const { t } = useTranslation();

  const requiredFolder = useCallback<Func<[fieldName: string, folder: unknown], true | string>>(
    (fieldName, folder) => {
      if (isIntegrationDrive(folder) || isIntegrationFolder(folder)) {
        return true;
      }

      return t(`validation:required`, { fieldName });
    },
    [t],
  );

  const isValidFolderPath = useCallback<Func<[fieldName: string, value: PatternFormField], true | string>>(
    (fieldName, value) => {
      const invalidItems = value.filter((item) => {
        if (typeof item !== 'string') {
          return false;
        }

        // Not has invalid characters
        if (/["*:<>?|\\]/.test(item)) {
          return true;
        }

        // Not has double slash
        return !!/\/\//.test(item);
      });

      return invalidItems.length ? t(`validation:invalid`, { fieldName }) : true;
    },
    [t],
  );

  return useMemo<TaskImportValidationRules<TFieldValues, TFieldName>>(
    () => ({
      folder: (fieldName) => ({
        required: (_value, { folder }) => requiredFolder(fieldName, folder),
      }),
      folderPath: (fieldName) => ({
        valid: (value) => isValidFolderPath(fieldName, value),
      }),
    }),
    [isValidFolderPath, requiredFolder],
  );
}
